import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from "axios";
import {
  Row, Col, Alert, Spin, Card, List, Typography, Space, message, Button, Collapse, Popconfirm, Modal, notification, Switch
} from 'antd';
import {
  StarFilled,
  LinkOutlined,
  BankOutlined,
  TeamOutlined,
  FileTextOutlined,
  UserOutlined,
  SettingOutlined,
  TagOutlined,
  RightOutlined,
  ReloadOutlined,
  DeleteOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import FeedWidget from '../feed/FeedWidget.jsx';
import PendingTasksWidget from './PendingTasksWidget.jsx';
import TodoWidget from './TodoWidget.jsx';
import { varsayilanDuzen, duzeniBirlestir, tanimBul } from './panoWidgetlari';
import Chatbot from './Chatbot.jsx';
import { API_BASE_URL } from '../../utils/config';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const InlinePivotChart = ({ pivotId, darkMode }) => {
  const { getAuthHeaders } = useAuth();
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [pivotConfig, setPivotConfig] = useState(null);
  const [googleChartsLoaded, setGoogleChartsLoaded] = useState(false);

  useEffect(() => {
    const initializeGoogleCharts = () => {
      if (window.google && window.google.charts) {
        window.google.charts.load('current', { 'packages': ['corechart'] });
        window.google.charts.setOnLoadCallback(() => {
          setGoogleChartsLoaded(true);
        });
      } else {
        const checkGoogleCharts = setInterval(() => {
          if (window.google && window.google.charts) {
            clearInterval(checkGoogleCharts);
            window.google.charts.load('current', { 'packages': ['corechart'] });
            window.google.charts.setOnLoadCallback(() => {
              setGoogleChartsLoaded(true);
            });
          }
        }, 200);    
        setTimeout(() => clearInterval(checkGoogleCharts), 15000);
      }
    };

    initializeGoogleCharts();
    const timeoutId = setTimeout(initializeGoogleCharts, 1000);
    
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (pivotId) {
      loadPivotData();
    }
  }, [pivotId]);

  useEffect(() => {
    if (googleChartsLoaded && chartData && pivotConfig && chartRef.current) {
      drawChart();
    }
  }, [googleChartsLoaded, chartData, pivotConfig, darkMode]);

  const loadPivotData = async () => {
    setLoading(true);
    try {
      const configResponse = await axios.get(`${API_BASE_URL}/pivot/get/${pivotId}`, {
        headers: getAuthHeaders()
      });

      if (configResponse.data.success && configResponse.data.pivot) {
        const pivot = configResponse.data.pivot;
        setPivotConfig(pivot);
        const snapshot = pivot.chart_snapshot;
        if (snapshot && Array.isArray(snapshot.data) && snapshot.data.length > 1) {
          setChartData(snapshot.data);
          return;
        }
      }
    } catch (error) {
      console.error("Error loading pivot:", error);
      message.error("Grafik yapılandırması yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const drawChart = () => {
    if (!window.google || !window.google.visualization || !chartRef.current || !chartData) {
      return;
    }

    try {
      const data = window.google.visualization.arrayToDataTable(chartData);
      const isMobile = window.innerWidth < 768;
      const options = {
        title: pivotConfig.title,
        height: isMobile ? 280 : 350,
        backgroundColor: darkMode ? '#1f1f1f' : '#ffffff',
        titleTextStyle: {
          color: darkMode ? '#ffffff' : '#000000',
          fontSize: isMobile ? 13 : 16
        },
        legend: {
          position: isMobile ? 'bottom' : 'right',
          textStyle: {
            color: darkMode ? '#ffffff' : '#000000',
            fontSize: isMobile ? 11 : 12
          }
        },
        chartArea: {
          width: isMobile ? '85%' : '75%',
          height: isMobile ? '60%' : '70%'
        }
      };

      const chartType = (pivotConfig.chart_snapshot?.type) || pivotConfig.chart_type;
      
      if (chartType === 'pie') {
        options.pieSliceText = 'value';
        options.sliceVisibilityThreshold = 0;
      } else if (chartType === 'bar' || chartType === 'bar_h') {
        options.hAxis = {
          textStyle: { color: darkMode ? '#ffffff' : '#000000' },
          gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
        };
        options.vAxis = {
          textStyle: { color: darkMode ? '#ffffff' : '#000000' }
        };
      } else if (chartType === 'column') {
        options.vAxis = {
          textStyle: { color: darkMode ? '#ffffff' : '#000000' },
          gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
        };
        options.hAxis = {
          textStyle: { color: darkMode ? '#ffffff' : '#000000' }
        };
      } else if (chartType === 'line' || chartType === 'area') {
        options.vAxis = {
          textStyle: { color: darkMode ? '#ffffff' : '#000000' },
          gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
        };
        options.hAxis = {
          textStyle: { color: darkMode ? '#ffffff' : '#000000' },
          gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
        };
      }

      let chart;
      switch (chartType) {
        case 'pie':
          chart = new window.google.visualization.PieChart(chartRef.current);
          break;
        case 'bar':
        case 'bar_h':
          chart = new window.google.visualization.BarChart(chartRef.current);
          break;
        case 'column':
          chart = new window.google.visualization.ColumnChart(chartRef.current);
          break;
        case 'line':
          chart = new window.google.visualization.LineChart(chartRef.current);
          break;
        case 'area':
          chart = new window.google.visualization.AreaChart(chartRef.current);
          break;
        default:
          chart = new window.google.visualization.ColumnChart(chartRef.current);
      }

      chart.draw(data, options);
    } catch (error) {
      console.error("Error drawing chart:", error);
      message.error("Grafik çizilemedi");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '10px' }}>Grafik yükleniyor...</div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
        <FileTextOutlined style={{ fontSize: '24px', marginBottom: '10px' }} />
        <div>Veri bulunamadı</div>
        <div style={{ fontSize: '12px', marginTop: '5px' }}>
          {pivotConfig ? `SQL: ${pivotConfig.sql_query}` : 'Konfigürasyon yükleniyor...'}
        </div>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={loadPivotData}
          style={{ marginTop: '10px' }}
        >
          Yeniden Dene
        </Button>
      </div>
    );
  }

  const isMobile = window.innerWidth < 768;
  
  return (
    <div style={{ 
      padding: isMobile ? '5px' : '10px',
      backgroundColor: darkMode ? '#1f1f1f' : '#ffffff',
      borderRadius: '8px'
    }}>
      <div 
        ref={chartRef} 
        style={{ 
          width: '100%', 
          minHeight: isMobile ? '300px' : '400px',
          backgroundColor: darkMode ? '#1f1f1f' : '#ffffff'
        }} 
      />
    </div>
  );
};

const SavedPivotsWidget = ({ darkMode, navigate }) => {
  const { getAuthHeaders } = useAuth();
  const [pivots, setPivots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedPivot, setExpandedPivot] = useState(null);

  useEffect(() => {
    loadPivots();
  }, []);

  const loadPivots = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/pivot/my-pivots`, {
        headers: getAuthHeaders(),
        params: { limit: 5 }
      });
      setPivots(response.data || []);
    } catch (error) {
      console.error("Error loading pivots:", error);
    } finally {
      setLoading(false);
    }
  };

  const deletePivot = async (pivotId) => {
    try {
      await axios.delete(`${API_BASE_URL}/pivot/delete/${pivotId}`, {
        headers: getAuthHeaders()
      });
      message.success('Grafik silindi');
      setPivots(prev => prev.filter(p => p.id !== pivotId));
      if (expandedPivot === pivotId) setExpandedPivot(null);
    } catch (error) {
      console.error('Delete pivot error:', error);
      message.error('Grafik silinemedi');
    }
  };

  const togglePivot = (pivotId) => {
    if (expandedPivot === pivotId) {
      setExpandedPivot(null);
    } else {
      setExpandedPivot(pivotId);
    }
  };

  if (pivots.length === 0) return null;

  const isMobile = window.innerWidth < 768;
  
  return (
    <Card
      title={
        <Space>
          <FileTextOutlined style={{ color: '#52c41a' }} />
          <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
            Kaydedilmiş Pivot Tablolar
          </Text>
        </Space>
      }
      style={{ marginBottom: isMobile ? '16px' : '24px' }}
      bodyStyle={{ padding: isMobile ? '8px' : '12px' }}
    >
      <Spin spinning={loading}>
        <List
          dataSource={pivots}
          renderItem={(pivot) => (
            <List.Item
              style={{
                padding: '0',
                border: 'none',
                background: darkMode ? 'transparent' : 'transparent'
              }}
            >
              <div style={{ width: '100%' }}>
                <div
                  style={{
                    cursor: 'pointer',
                    padding: isMobile ? '4px' : '6px',
                    borderRadius: '6px',
                    transition: 'all 0.2s',
                    background: darkMode ? 'transparent' : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => togglePivot(pivot.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode ? '#1f1f1f' : '#fafafa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '500' }}>
                          {pivot.title}
                        </Text>
                        <Popconfirm
                          title="Grafik silinsin mi?"
                          okText="Sil"
                          cancelText="Vazgeç"
                          onConfirm={(e) => { e?.stopPropagation?.(); deletePivot(pivot.id); }}
                          onCancel={(e) => e?.stopPropagation?.()}
                        >
                          <Button
                            type="text"
                            size={isMobile ? 'small' : 'middle'}
                            icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Popconfirm>
                      </div>
                    }
                    description={
                      <Space size="small" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                        <Text type="secondary">
                          {pivot.chart_type === "flat" ? "📊 Tablo" :
                            pivot.chart_type === "pie" ? "🥧 Pasta" :
                              pivot.chart_type === "bar" || pivot.chart_type === "bar_h" ? "📊 Bar" :
                                pivot.chart_type === "column" ? "📊 Kolon" :
                                  pivot.chart_type === "line" ? "📈 Çizgi" :
                                    pivot.chart_type === "area" ? "📉 Alan" : pivot.chart_type}
                        </Text>
                        <Text type="secondary">•</Text>
                        <Text type="secondary">
                          {new Date(pivot.created_at).toLocaleDateString('tr-TR')}
                        </Text>
                      </Space>
                    }
                  />
                </div>

                {expandedPivot === pivot.id && (
                  <div style={{
                    marginTop: isMobile ? '8px' : '10px',
                    border: darkMode ? '1px solid #303030' : '1px solid #f0f0f0',
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <InlinePivotChart pivotId={pivot.id} darkMode={darkMode} />
                  </div>
                )}
              </div>
            </List.Item>
          )}
          
        />
      </Spin>
    </Card>
  );
};


// Menü ikonu adı -> antd bileşeni. Bu iki yardımcı önce DashboardContent'in
// İÇİNDE tanımlıydı; QuickAccessWidget dışarı çıkarılınca kapsam dışında kaldılar
// ve anasayfa `getIconComponent is not defined` ile çöküyordu (25.08 md, madde 8).
// İkisi de saf yardımcı, yeri modül kapsamı.
const iconMap = {
  'LinkOutlined': LinkOutlined,
  'BankOutlined': BankOutlined,
  'TeamOutlined': TeamOutlined,
  'FileTextOutlined': FileTextOutlined,
  'UserOutlined': UserOutlined,
  'TagOutlined': TagOutlined,
  'QuestionCircleOutlined': QuestionCircleOutlined,
  'SettingOutlined': SettingOutlined,
};

const getIconComponent = (iconName) => {
  if (!iconName) return null;
  const IconComponent = iconMap[iconName];
  return IconComponent ? React.createElement(IconComponent) : null;
};

/**
 * Hızlı Erişim (favori menüler) — 25.08 md madde 8 kapsamında DashboardContent
 * içinden ayrıldı; artık widget kayıt defterinden çizilebilen bağımsız bir
 * bileşen. Favori yoksa hiçbir şey çizmez (eski davranış korunur).
 */
const QuickAccessWidget = ({ favoriteMenus, darkMode, isMobile, navigate }) => {
  if (!favoriteMenus || favoriteMenus.length === 0) return null;
  return (
            <Card
              title={
                <Space>
                  <StarFilled style={{ color: '#faad14' }} />
                  <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                    Hızlı Erişim
                  </Text>
                </Space>
              }
              style={{ marginBottom: isMobile ? '16px' : '24px' }}
              bodyStyle={{ padding: isMobile ? '8px' : '12px' }}
            >
              <List
                grid={{
                  gutter: isMobile ? 8 : 16,
                  xs: 2,
                  sm: 3,
                  md: 3,
                  lg: 4,
                  xl: 5,
                  xxl: 6,
                }}
                dataSource={favoriteMenus.filter(item => item.name && item.url_path)}
                renderItem={(item) => {
                  const menuRoute = item.url_path || item.route || '/home';
                  const menuIcon = getIconComponent(item.icon);
                  return (
                    <List.Item>
                      <Card
                        hoverable
                        onClick={() => navigate(menuRoute)}
                        style={{
                          textAlign: 'center',
                          borderRadius: isMobile ? '6px' : '8px',
                          background: darkMode ? '#1f1f1f' : '#fafafa',
                          border: darkMode ? '1px solid #303030' : '1px solid #f0f0f0'
                        }}
                        bodyStyle={{ padding: isMobile ? '12px 6px' : '16px 8px' }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{ fontSize: isMobile ? '20px' : '24px', color: '#1890ff' }}>
                            {menuIcon || <TagOutlined />}
                          </div>
                          <Text
                            style={{
                              fontSize: isMobile ? '11px' : '12px',
                              display: 'block'
                            }}
                          >
                            {item.name}
                          </Text>
                          <RightOutlined style={{ fontSize: isMobile ? '8px' : '10px', color: '#8c8c8c' }} />
                        </Space>
                      </Card>
                    </List.Item>
                  );
                }}
              />
            </Card>
  );
};


/**
 * Widget ekle/çıkar penceresi — 25.08 md, madde 8.
 * Görünürlük kullanıcı bazlı saklanır (PUT /pano/tercih).
 */
const WidgetAyarModal = ({ open, onClose, duzen, onDegis }) => (
  <Modal
    open={open}
    onCancel={onClose}
    title="Anasayfa Widget'ları"
    footer={<Button onClick={onClose}>Kapat</Button>}
  >
    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
      Görmek istediğiniz widget'ları seçin. Tercihiniz hesabınıza kaydedilir ve
      diğer cihazlarınızda da geçerli olur.
    </Text>
    <List
      size="small"
      dataSource={duzen}
      renderItem={(d) => {
        const tanim = tanimBul(d.anahtar);
        if (!tanim) return null;
        return (
          <List.Item
            actions={[
              <Switch
                key="ac"
                size="small"
                checked={d.gorunur}
                onChange={(v) =>
                  onDegis(duzen.map((x) =>
                    x.anahtar === d.anahtar ? { ...x, gorunur: v } : x))
                }
              />,
            ]}
          >
            <List.Item.Meta title={tanim.ad} description={tanim.aciklama} />
          </List.Item>
        );
      }}
    />
  </Modal>
);

const DashboardContent = () => {
  const { darkMode } = useTheme();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [favoriteMenus, setFavoriteMenus] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // --- Widget düzeni (25.08 md, madde 8) --------------------------------
  const [duzen, setDuzen] = useState(varsayilanDuzen);
  const [widgetAyarAcik, setWidgetAyarAcik] = useState(false);
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [dismissedBirthdays, setDismissedBirthdays] = useState(() => {
    const saved = localStorage.getItem('dismissedBirthdays');
    return saved ? JSON.parse(saved) : [];
  });
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalPlatforms: 0,
      activeCabins: 0,
      totalRevenue: 0,
      pendingTasks: 0,
      totalLocations: 0,
      activeContracts: 0
    },
    alerts: []
  });
  const [completedJobModal, setCompletedJobModal] = useState({
    visible: false,
    jobId: null,
    meetingId: null,
    filename: null
  });

  const loadTodayBirthdays = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/list/yaklasan-dg`, {
        headers: getAuthHeaders(),
        params: { limit: 10 }
      });
      if (response.data && Array.isArray(response.data)) {
        const today = response.data.filter(person => {
          const daysRemaining = person.dg_kalan !== undefined && person.dg_kalan !== null
            ? parseInt(person.dg_kalan) 
            : 999;
          const isToday = daysRemaining === 0 || person.dg_kalan === 0 || person.dg_kalan === '0';
          return isToday;
        });
        
        const filtered = today.filter(person => {
          const uniqueKey = person.kullanici_no;
          return !dismissedBirthdays.includes(uniqueKey);
        });
        setTodayBirthdays(filtered);
      } else {
        setTodayBirthdays([]);
      }
    } catch (error) {
      console.error("Error loading today's birthdays:", error);
      setTodayBirthdays([]);
    }
  };

  const loadFavoriteMenus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/feed/favorites/menus`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        const favorites = response.data.favorites || [];
        setFavoriteMenus(favorites);
      }
    } catch (error) {
      console.error('Favori menüler yüklenirken hata:', error);
    }
  }, [getAuthHeaders]);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      const [
        feedInitResponse,
        feedResponse,
        favoritesResponse
      ] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/feed/posts`, {
          headers: getAuthHeaders(),
          params: { page: 0, limit: 20 }
        }),
        axios.post(`${API_BASE_URL}/feed/favorites/init-table`, {}, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_BASE_URL}/feed/favorites/menus`, {
          headers: getAuthHeaders()
        })
      ]);

      if (favoritesResponse.status === 'fulfilled' && favoritesResponse.value.data.success) {
        const favorites = favoritesResponse.value.data.favorites || [];
        setFavoriteMenus(favorites);
      }

      setDashboardData({
        stats: {
          totalPlatforms: 0,
          activeCabins: 0,
          totalRevenue: 0,
          pendingTasks: 0,
          totalLocations: 0,
          activeContracts: 0
        },
        alerts: []
      });

      await loadTodayBirthdays();

    } catch (error) {
      console.error('Dashboard veri yükleme hatası:', error);

    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    const handleFavoriteMenuChange = () => {
      loadFavoriteMenus();
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('favoriteMenuChanged', handleFavoriteMenuChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('favoriteMenuChanged', handleFavoriteMenuChange);
    };
  }, [getAuthHeaders]);

  useEffect(() => {
    const checkPendingJobs = async () => {
      const pendingJobs = JSON.parse(localStorage.getItem('pendingAudioJobs') || '[]');
      if (pendingJobs.length === 0) return;

      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const updatedJobs = [];
      
      for (const job of pendingJobs) {
        try {
          const response = await fetch(`${API_BASE_URL}/speech2text/process-audio/status/${job.jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!response.ok) {
            updatedJobs.push(job); 
            continue;
          }

          const statusData = await response.json();

          if (statusData.status === 'completed') {
            try {
              const resultResponse = await fetch(`${API_BASE_URL}/speech2text/process-audio/result/${job.jobId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (resultResponse.ok) {
                const result = await resultResponse.json();
                
                setCompletedJobModal({
                  visible: true,
                  jobId: job.jobId,
                  meetingId: result.db_id,
                  filename: job.filename
                });
                
                const notificationKey = `audio-job-${job.jobId}`;
                const handleViewClick = () => {
                  try {
                    notification.destroy(notificationKey);
                  } catch (e) {
                    console.warn('Notification destroy error:', e);
                  }
                  navigate(`/toplanti_ozet/${result.db_id}`);
                };
                
                notification.success({
                  key: notificationKey,
                  message: 'Toplantı Transkripti Hazır',
                  description: `${job.filename} dosyası için transkript ve özet oluşturuldu.`,
                  duration: 0, 
                  btn: (api) => {
                    const closeFn = typeof api?.close === 'function' ? api.close : null;
                    return (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          if (closeFn) {
                            try {
                              closeFn();
                            } catch (e) {
                              console.warn('Notification close error:', e);
                            }
                          }
                          handleViewClick();
                        }}
                      >
                        Görüntüle
                      </Button>
                    );
                  },
                  onClick: handleViewClick,
                  placement: 'topRight'
                });
                
                message.success('Toplantı transkripti hazır!');
              } else {
                updatedJobs.push(job); 
              }
            } catch (error) {
              console.error('Result fetch hatası:', error);
              updatedJobs.push(job);
            }
          } else if (statusData.status === 'error') {
            console.error('Job hatası:', statusData.error);
            message.error(`İşlem hatası: ${statusData.error || 'Bilinmeyen hata'}`);
          } else {
            updatedJobs.push(job); 
          }
        } catch (error) {
          console.error('Job kontrol hatası:', error);
          updatedJobs.push(job); 
        }
      }

      localStorage.setItem('pendingAudioJobs', JSON.stringify(updatedJobs));
    };

    checkPendingJobs();

    const interval = setInterval(checkPendingJobs, 10000);

    return () => clearInterval(interval);
  }, []);

  // Kullanıcı düzenini getir. Tercih yoksa/migration uygulanmadıysa varsayılan
  // düzenle devam edilir — anasayfa hiçbir durumda boş kalmaz.
  useEffect(() => {
    let iptal = false;
    axios
      .get(`${API_BASE_URL}/pano/tercih`)
      .then((r) => { if (!iptal) setDuzen(duzeniBirlestir(r.data?.duzen)); })
      .catch(() => { if (!iptal) setDuzen(varsayilanDuzen()); });
    return () => { iptal = true; };
  }, []);

  const duzeniKaydet = useCallback(async (yeni) => {
    setDuzen(yeni);                       // iyimser: anında uygulansın
    try {
      await axios.put(`${API_BASE_URL}/pano/tercih`, { duzen: yeni });
    } catch (e) {
      // 501 = migration henüz uygulanmadı; düzen bu oturumda geçerli kalır.
      if (e?.response?.status !== 501) message.error('Widget tercihi kaydedilemedi');
    }
  }, []);

  const gorunurWidgetlar = React.useMemo(() => {
    const acik = duzen.filter((d) => d.gorunur);
    return {
      tam: acik.filter((d) => tanimBul(d.anahtar)?.genislik === 'tam'),
      yarim: acik.filter((d) => tanimBul(d.anahtar)?.genislik === 'yarim'),
    };
  }, [duzen]);

  const widgetCiz = useCallback((anahtar) => {
    switch (anahtar) {
      case 'bekleyen_surecler':
        return <PendingTasksWidget />;
      case 'feed':
        return <FeedWidget limit={20} showTitle={true} showPostForm={true} />;
      case 'todo':
        return <TodoWidget darkMode={darkMode} />;
      case 'hizli_erisim':
        return (
          <QuickAccessWidget
            favoriteMenus={favoriteMenus}
            darkMode={darkMode}
            isMobile={isMobile}
            navigate={navigate}
          />
        );
      case 'kayitli_pivotlar':
        return <SavedPivotsWidget darkMode={darkMode} navigate={navigate} />;
      default:
        return null;
    }
  }, [darkMode, favoriteMenus, isMobile, navigate]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      {todayBirthdays && todayBirthdays.length > 0 && (
        <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
          {todayBirthdays.map((person, index) => {
            const personName = person.ad_soyad;
            console.log("personName", personName);
            const uniqueKey = person.kullanici_no;
            const handleClose = () => {
              const updated = [...dismissedBirthdays, uniqueKey];
              setDismissedBirthdays(updated);
              localStorage.setItem('dismissedBirthdays', JSON.stringify(updated));
              setTodayBirthdays(prev => prev.filter(p => p.kullanici_no !== uniqueKey));
            };
            
            return (
              <Alert
                key={uniqueKey}
                message={`Bugün ${personName} kişisinin doğum günü! 🎉`}
                type="success"
                icon={<CalendarOutlined />}
                showIcon
                closable
                onClose={handleClose}
                style={{ marginBottom: '8px' }}
              />
            );
          })}
        </div>
      )}

      {/* Uyarılar */}
      {dashboardData.alerts && dashboardData.alerts.length > 0 && (
        <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
          {dashboardData.alerts.map((alert, index) => (
            <Alert
              key={`alert-${index}-${alert.message}`}
              message={alert.message}
              type={alert.type}
              icon={alert.icon}
              showIcon
              style={{ marginBottom: '8px' }}
            />
          ))}
        </div>
      )}

      {/* Widget'lar — düzen `panoWidgetlari.js` kayıt defterinden gelir ve
          kullanıcı bazlı saklanır (25.08 md, madde 8). Önceden burada elle
          yazılmış sabit JSX vardı; ekleme/çıkarma mümkün değildi. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button
          size="small"
          icon={<AppstoreOutlined />}
          onClick={() => setWidgetAyarAcik(true)}
        >
          Widget'ları Düzenle
        </Button>
      </div>

      {gorunurWidgetlar.tam.map((w) => (
        <React.Fragment key={w.anahtar}>{widgetCiz(w.anahtar)}</React.Fragment>
      ))}

      <Row gutter={isMobile ? [12, 12] : [16, 16]}>
        {gorunurWidgetlar.yarim.map((w, idx) => (
          <Col xs={24} lg={12} key={w.anahtar}>
            {widgetCiz(w.anahtar)}
          </Col>
        ))}
      </Row>

      <WidgetAyarModal
        open={widgetAyarAcik}
        onClose={() => setWidgetAyarAcik(false)}
        duzen={duzen}
        onDegis={duzeniKaydet}
      />


      {/* Chatbot */}
      <Chatbot />

      {/* İşlem Tamamlandı Modal */}
      <Modal
        title="İşlem Tamamlandı"
        open={completedJobModal.visible}
        onOk={() => {
          setCompletedJobModal({ visible: false, jobId: null, meetingId: null, filename: null });
          if (completedJobModal.meetingId) {
            navigate(`/toplanti_ozet/${completedJobModal.meetingId}`);
          }
        }}
        onCancel={() => {
          setCompletedJobModal({ visible: false, jobId: null, meetingId: null, filename: null });
        }}
        okText="Görüntüle"
        cancelText="Kapat"
      >
        <p>Toplantı transkripti ve özeti başarıyla oluşturuldu!</p>
        {completedJobModal.filename && (
          <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
            Dosya: {completedJobModal.filename}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default DashboardContent;